import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Users, 
  Zap, 
  TrendingUp, 
  BadgePercent,
  CheckCircle2,
  ArrowRight,
  Rocket,
  Globe,
  CreditCard,
  Shield,
  Sparkles,
  Calculator,
  Star,
  Crown,
  Trophy,
  ChevronRight,
  Check,
  X,
  Gift,
  Headphones,
  BookOpen,
  Smartphone,
  Search,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import theme from '../theme.config';
import HeroVisual from '../components/HeroVisual';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reseller Packages
const PACKAGES = [
  {
    id: 'pro',
    name: 'Pro',
    icon: Zap,
    color: 'secondary',
    monthlyPrice: 99000,
    yearlyPrice: 799000,
    discount: 33,
    features: [
      { name: 'Akses Harga Reseller', included: true },
      { name: 'Dashboard Reseller', included: true },
      { name: 'Subdomain Gratis', included: true },
      { name: 'Custom Domain', included: false },
      { name: 'Unlimited Transaksi', included: true },
      { name: 'Support WhatsApp', included: true },
      { name: 'Support Prioritas', included: false },
      { name: 'Reseller Academy', included: false },
      { name: 'Kupon Diskon Custom', included: false },
    ]
  },
  {
    id: 'legend',
    name: 'Legend',
    icon: Crown,
    color: 'primary',
    popular: true,
    monthlyPrice: 199000,
    yearlyPrice: 1599000,
    discount: 33,
    features: [
      { name: 'Akses Harga Reseller', included: true },
      { name: 'Dashboard Reseller', included: true },
      { name: 'Subdomain Gratis', included: true },
      { name: 'Custom Domain', included: true },
      { name: 'Unlimited Transaksi', included: true },
      { name: 'Support WhatsApp', included: true },
      { name: 'Support Prioritas', included: true },
      { name: 'Reseller Academy', included: true },
      { name: 'Kupon Diskon Custom', included: false },
    ]
  },
  {
    id: 'supreme',
    name: 'Supreme',
    icon: Trophy,
    color: 'accent',
    monthlyPrice: 349000,
    yearlyPrice: 2799000,
    discount: 33,
    features: [
      { name: 'Akses Harga Reseller', included: true },
      { name: 'Dashboard Reseller', included: true },
      { name: 'Subdomain Gratis', included: true },
      { name: 'Custom Domain', included: true },
      { name: 'Unlimited Transaksi', included: true },
      { name: 'Support WhatsApp', included: true },
      { name: 'Support Prioritas', included: true },
      { name: 'Reseller Academy', included: true },
      { name: 'Kupon Diskon Custom', included: true },
    ]
  }
];

const BENEFITS = [
  { 
    icon: CreditCard, 
    title: 'Tanpa Deposit', 
    description: 'Mulai tanpa modal deposit. Bayar sesuai paket yang dipilih.',
    color: 'primary'
  },
  { 
    icon: Zap, 
    title: 'Sistem Otomatis', 
    description: 'Semua proses top-up berjalan otomatis 24/7 tanpa ribet.',
    color: 'secondary'
  },
  { 
    icon: Globe, 
    title: 'Website Sendiri', 
    description: 'Dapatkan website profesional dengan domain sendiri.',
    color: 'success'
  },
  { 
    icon: TrendingUp, 
    title: 'Margin Tinggi', 
    description: 'Harga reseller jauh lebih murah, margin keuntungan besar.',
    color: 'accent'
  },
  { 
    icon: Users, 
    title: 'Pemula Friendly', 
    description: 'Tidak perlu keahlian teknis. Kami bantu setup semuanya.',
    color: 'primary'
  },
  { 
    icon: Shield, 
    title: 'Garansi 7 Hari', 
    description: 'Tidak cocok? Uang kembali 100% dalam 7 hari pertama.',
    color: 'destructive'
  },
];

const TESTIMONIALS = [
  {
    name: 'Rizky Pratama',
    store: 'topupgame.id',
    avatar: 'RP',
    rating: 5,
    comment: `Sejak jadi reseller ${theme.brand.name}, penghasilan naik 3x lipat. Sistemnya mudah dan support-nya responsif banget!`,
    profit: 'Rp 15.000.000/bulan'
  },
  {
    name: 'Siti Nurhaliza',
    store: 'diamondstore.com',
    avatar: 'SN',
    rating: 5,
    comment: 'Awalnya ragu, tapi setelah 2 bulan omset tembus 50 juta! Recommended banget buat yang mau bisnis online.',
    profit: 'Rp 8.500.000/bulan'
  },
  {
    name: 'Ahmad Fauzi',
    store: 'gg-topup.id',
    avatar: 'AF',
    rating: 5,
    comment: 'Proses withdrawnya cepat, profit calculator-nya akurat. Cocok untuk pemula yang mau mulai bisnis.',
    profit: 'Rp 12.000.000/bulan'
  },
];

const STATS = [
  { label: 'Total Omzet Reseller', value: 'Rp 1.9M+', suffix: '' },
  { label: 'Total Transaksi', value: '40', suffix: ' Miliar' },
  { label: 'Reseller Aktif', value: '2,500', suffix: '+' },
  { label: 'Rating Kepuasan', value: '4.9', suffix: '/5' },
];

// Simulated taken domains for demo
const TAKEN_DOMAINS = {
  'com': ['topupgame', 'diamondstore', 'gamevoucher', 'topupku'],
  'id': ['topupgame', 'gg-topup'],
  'co.id': ['gamevoucher'],
  'net': ['topupgame'],
  'store': [],
};

// Domain extensions with prices
const DOMAIN_EXTENSIONS = [
  { ext: '.com', price: 150000, popular: true },
  { ext: '.id', price: 250000, popular: true },
  { ext: '.co.id', price: 200000, popular: false },
  { ext: '.net', price: 140000, popular: false },
  { ext: '.store', price: 180000, popular: true },
  { ext: '.shop', price: 170000, popular: false },
];

// Domain Checker Component
const DomainChecker = () => {
  const [domainInput, setDomainInput] = useState('');
  const [selectedExt, setSelectedExt] = useState('.com');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const checkDomain = async () => {
    if (!domainInput.trim()) {
      toast.error('Masukkan nama domain');
      return;
    }

    // Clean domain input (remove spaces, special chars)
    const cleanDomain = domainInput.toLowerCase().replace(/[^a-z0-9-]/g, '');
    
    setChecking(true);
    setResult(null);

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Check availability for all extensions
    const extensionResults = DOMAIN_EXTENSIONS.map(ext => {
      const extKey = ext.ext.replace('.', '');
      const takenList = TAKEN_DOMAINS[extKey] || [];
      const isAvailable = !takenList.includes(cleanDomain);
      return {
        ...ext,
        available: isAvailable,
        fullDomain: `${cleanDomain}${ext.ext}`
      };
    });

    // Generate suggestions if main domain is taken
    const mainExtKey = selectedExt.replace('.', '');
    const mainTaken = (TAKEN_DOMAINS[mainExtKey] || []).includes(cleanDomain);
    
    const suggestions = mainTaken ? [
      `${cleanDomain}-store`,
      `${cleanDomain}id`,
      `my${cleanDomain}`,
      `${cleanDomain}shop`,
      `get${cleanDomain}`,
    ].slice(0, 4) : [];

    setResult({
      domain: cleanDomain,
      selectedExt,
      mainAvailable: !mainTaken,
      suggestions,
      extensions: extensionResults,
    });

    setChecking(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      checkDomain();
    }
  };

  return (
    <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-border cyber-card relative overflow-hidden">
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/40" />
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center neon-magenta">
          <Globe className="w-6 h-6 text-secondary" />
        </div>
        <div>
          <h3 className="font-bold text-xl text-white uppercase tracking-wider">Cek Domain</h3>
          <p className="text-xs text-muted-foreground font-mono">Dapatkan domain sendiri untuk website Anda</p>
        </div>
      </div>

      {/* Search Input */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="Masukkan nama domain impianmu..."
            className="bg-black/50 border-border text-white pl-4 py-6 text-sm font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            onKeyPress={handleKeyPress}
            data-testid="domain-input"
          />
        </div>
        <select
          className="bg-black/50 border border-border rounded-lg px-4 py-3 text-white text-xs font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
          value={selectedExt}
          onChange={(e) => setSelectedExt(e.target.value)}
        >
          {DOMAIN_EXTENSIONS.map(ext => (
            <option key={ext.ext} value={ext.ext} className="bg-neutral-900">
              {ext.ext} - {formatPrice(ext.price)}/thn
            </option>
          ))}
        </select>
        <Button
          className="bg-secondary hover:bg-secondary/80 text-black font-mono text-xs uppercase px-8 neon-magenta"
          onClick={checkDomain}
          disabled={checking}
          data-testid="check-domain-btn"
        >
          {checking ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              [ CEK DOMAIN ]
            </>
          )}
        </Button>
      </div>

      {/* Quick Extension Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        {DOMAIN_EXTENSIONS.filter(e => e.popular).map(ext => (
          <button
            key={ext.ext}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedExt === ext.ext
                ? 'bg-secondary/20 text-secondary border border-secondary/30'
                : 'bg-black/30 text-gray-400 border border-white/10 hover:border-white/30'
            }`}
            onClick={() => setSelectedExt(ext.ext)}
          >
            {ext.ext}
          </button>
        ))}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
          {/* Main Result */}
          <div className={`p-5 rounded-xl border relative overflow-hidden ${
            result.mainAvailable 
              ? 'bg-primary/5 border-primary/30' 
              : 'bg-red-500/5 border-red-500/30'
          }`}>
            {result.mainAvailable ? (
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/70" />
            ) : (
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-red-500/70" />
            )}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {result.mainAvailable ? (
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center neon-green">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center neon-magenta">
                    <X className="w-6 h-6 text-red-500" />
                  </div>
                )}
                <div>
                  <p className="font-mono text-lg text-white font-bold">
                    {result.domain}{result.selectedExt}
                  </p>
                  <p className={`text-xs font-mono ${result.mainAvailable ? 'text-primary' : 'text-red-400'}`}>
                    {result.mainAvailable ? '✓ Domain tersedia untuk didaftarkan!' : '✗ Domain sudah terdaftar'}
                  </p>
                </div>
              </div>
              {result.mainAvailable && (
                <div className="flex items-center gap-3">
                  <div className="text-right font-mono">
                    <p className="text-[10px] text-muted-foreground">Harga per tahun</p>
                    <p className="text-base font-bold text-white">
                      {formatPrice(DOMAIN_EXTENSIONS.find(e => e.ext === result.selectedExt)?.price || 0)}
                    </p>
                  </div>
                  <Button 
                    className="bg-primary hover:bg-primary/80 text-black font-mono text-xs uppercase px-6 neon-green"
                    onClick={() => document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Daftarkan Domain
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Suggestions if taken */}
          {!result.mainAvailable && result.suggestions.length > 0 && (
            <div className="bg-black/20 rounded-xl p-4 border border-border/40">
              <p className="text-xs font-mono text-muted-foreground mb-3">Saran nama domain alternatif:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {result.suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-colors group"
                    onClick={() => setDomainInput(suggestion)}
                  >
                    <span className="font-mono text-xs text-white">{suggestion}{result.selectedExt}</span>
                    <Check className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* All Extensions Availability */}
          <div className="bg-black/20 rounded-xl p-4 border border-border/40">
            <p className="text-xs font-mono text-muted-foreground mb-3">Ketersediaan ekstensi lainnya untuk "{result.domain}":</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {result.extensions.map((ext, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    ext.available 
                      ? 'bg-black/30 border border-border/30 hover:border-primary/30' 
                      : 'bg-black/20 opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {ext.available ? (
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    ) : (
                      <X className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-mono text-xs text-white">{ext.fullDomain}</span>
                    {ext.popular && ext.available && (
                      <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded-full font-mono">Populer</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {ext.available && (
                      <>
                        <span className="font-mono text-xs text-muted-foreground">
                          {formatPrice(ext.price)}/thn
                        </span>
                        <Button 
                          size="sm" 
                          className="text-[10px] h-7 bg-primary/20 text-primary hover:bg-primary/30 border-0 font-mono"
                          onClick={() => {
                            setSelectedExt(ext.ext);
                            document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          Pilih
                        </Button>
                      </>
                    )}
                    {!ext.available && (
                      <span className="text-xs text-muted-foreground font-mono">Tidak tersedia</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Domain Package Info */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-4 border border-primary/20 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-white font-mono text-xs font-bold mb-1">Domain Sudah Termasuk dalam Paket!</p>
                <p className="text-xs text-muted-foreground font-mono">
                  Pilih paket Legend atau Supreme untuk mendapatkan domain gratis selama 1 tahun pertama. 
                  Kami akan membantu setup DNS dan SSL untuk website Anda.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      {!result && (
        <div className="bg-black/20 rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-3">Tips memilih nama domain:</p>
          <ul className="text-xs text-muted-foreground space-y-2">
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-success" />
              Gunakan nama yang mudah diingat dan dieja
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-success" />
              Pilih ekstensi .com atau .id untuk pasar Indonesia
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-success" />
              Hindari angka dan tanda hubung berlebihan
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-success" />
              Pilih nama yang mencerminkan bisnis game/voucher Anda
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default function Reseller() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('legend');
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStep, setPaymentStep] = useState('form'); // form | paying
  
  // Profit Calculator State
  const [sellingPrice, setSellingPrice] = useState(25000);
  const [dailySales, setDailySales] = useState(10);
  const basePrice = 19000; // Reseller price

  const calculateProfit = () => {
    const profitPerSale = sellingPrice - basePrice;
    const dailyProfit = profitPerSale * dailySales;
    const monthlyProfit = dailyProfit * 30;
    return { profitPerSale, dailyProfit, monthlyProfit };
  };

  const profit = calculateProfit();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Silakan login terlebih dahulu');
      navigate('/login?redirect=/reseller');
      return;
    }

    if (!phone) {
      toast.error('Nomor telepon harus diisi');
      return;
    }

    setLoading(true);
    try {
      // 1. Create subscription
      const subRes = await axios.post(
        `${API_URL}/api/reseller/subscribe`,
        { plan: selectedPackage, period: billingPeriod, phone, business_name: businessName || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { order_id, amount } = subRes.data;

      // 2. Create payment via Ayolinx
      const payRes = await axios.post(`${API_URL}/api/payment/create`, {
        order_id,
        amount,
        customer_name: user.name,
        customer_email: user.email,
        payment_method: 'qris',
      });

      if (payRes.data.success) {
        setPaymentData({ ...payRes.data.data, orderId: order_id, amount });
        setPaymentStep('paying');
        toast.success('Silakan selesaikan pembayaran');
      } else {
        toast.error(payRes.data.message || 'Gagal membuat pembayaran');
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      toast.error(error.response?.data?.detail || 'Gagal memproses pendaftaran');
    } finally {
      setLoading(false);
    }
  };

  // Already a reseller
  if (user?.role === 'reseller') {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center px-4 cyber-grid">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 neon-green">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-[10px] text-primary tracking-widest uppercase">RESELLER_ACCESS_GRANTED</span>
          </div>
          <h1 className="font-bold text-2xl text-white uppercase mb-2 tracking-wider">
            Koneksi Reseller Aktif!
          </h1>
          <p className="text-muted-foreground text-sm font-mono mb-6">
            Akses terminal command center reseller untuk meluncurkan transmisi kargo Anda.
          </p>
          <Button
            className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green text-xs"
            onClick={() => navigate('/reseller/dashboard')}
          >
            Buka Command Center
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28 cyber-grid">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/15 rounded-full blur-3xl" />
        
        {/* Site-specific decorative visuals (replace legacy mascots) */}
        <div className="absolute left-0 bottom-0 hidden xl:block opacity-30 z-0">
          <HeroVisual compact />
        </div>
        <div className="absolute right-0 top-20 hidden xl:block opacity-30 z-0">
          <HeroVisual compact />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 text-center z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs text-primary font-mono tracking-widest uppercase">ASTRAFLUX_PARTNERSHIP_INITIATIVE_2026</span>
          </div>
          
          <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase mb-6 tracking-wider leading-none">
            Mulai Bisnis Top Up Game <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-accent">
              Tanpa Hambatan & Bebas Deposit
            </span>
          </h1>
          
          <p className="text-gray-300 text-sm md:text-base font-mono max-w-3xl mx-auto mb-8">
            Bergabung dengan aliansi 2,500+ reseller aktif. Dapatkan terminal website mandiri, alokasi energi khusus, 
            dan transmisi kargo otomatis 24 jam. Jalur dimulai dari <span className="text-primary font-bold">Rp 99.000/bulan</span>.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12 font-mono text-xs">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest px-8 neon-green"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-pricing"
            >
              <Rocket className="w-5 h-5 mr-2" />
              [ LIHAT HARGA PAKET ]
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-secondary/50 text-secondary hover:bg-secondary/10 uppercase tracking-widest"
              onClick={() => document.getElementById('domain-checker').scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-domain"
            >
              <Globe className="w-5 h-5 mr-2" />
              [ CEK DOMAIN ]
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 uppercase tracking-widest"
              onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
            >
              <Calculator className="w-5 h-5 mr-2" />
              [ ESTIMASI PROFIT ]
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {STATS.map((stat, index) => (
              <div key={index} className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-primary/15 relative overflow-hidden cyber-card">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
                <p className="font-mono text-2xl md:text-3xl font-bold text-white">
                  {stat.value}<span className="text-secondary">{stat.suffix}</span>
                </p>
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-mono text-[10px] text-primary tracking-widest uppercase">STATION_ADVANTAGES</span>
            </div>
            <h2 className="font-bold text-3xl md:text-4xl text-white uppercase mb-4 tracking-wider">
              Kenapa Pilih <span className="text-primary">{theme.brand.name}</span>?
            </h2>
            <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto">
              Platform reseller all-in-one dengan fitur lengkap untuk memulai bisnis top up game
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((benefit, index) => {
              const Icon = benefit.icon;
              const colorClass = {
                'primary': 'bg-primary/20 text-primary neon-green',
                'secondary': 'bg-secondary/20 text-secondary neon-magenta',
                'success': 'bg-primary/20 text-primary neon-green',
                'accent': 'bg-accent/20 text-accent',
                'destructive': 'bg-destructive/20 text-destructive',
              }[benefit.color];
              
              return (
                <div 
                  key={index} 
                  className="group bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r-2 border-secondary/40" />
                  <div className={`w-14 h-14 rounded-xl ${colorClass} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-lg text-white mb-2 uppercase tracking-wide">{benefit.title}</h3>
                  <p className="text-muted-foreground text-xs font-mono">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Domain Checker Section */}
      <section id="domain-checker" className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
              <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">DOMAIN_DNS_PROVISION</span>
            </div>
            <h2 className="font-bold text-3xl md:text-4xl text-white uppercase mb-4 tracking-wider">
              Cek <span className="text-secondary">Domain</span> Impianmu
            </h2>
            <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto">
              Pilih nama domain unik untuk website reseller Anda. Subdomain gratis untuk semua paket!
            </p>
          </div>
          
          <DomainChecker />
        </div>
      </section>

      {/* Profit Calculator */}
      <section id="calculator" className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">MARGIN_CALCULATOR_NODE</span>
              </div>
              <h2 className="font-bold text-3xl md:text-4xl text-white uppercase mb-4 tracking-wider">
                <Calculator className="inline w-8 h-8 text-secondary mr-2" />
                Kalkulator Keuntungan
              </h2>
              <p className="text-muted-foreground font-mono text-sm mb-8">
                Hitung estimasi penghasilan Anda sebagai reseller {theme.brand.name}. 
                Geser slider transmisi untuk melihat potensi yield keuntungan orbital!
              </p>

              <div className="space-y-8 font-mono text-xs">
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-gray-300">Harga Jual per Item</Label>
                    <span className="text-primary font-bold">{formatPrice(sellingPrice)}</span>
                  </div>
                  <input
                    type="range"
                    min="20000"
                    max="50000"
                    step="1000"
                    value={sellingPrice}
                    onChange={(e) => setSellingPrice(Number(e.target.value))}
                    className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>Rp 20.000</span>
                    <span>Rp 50.000</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-gray-300">Penjualan per Hari</Label>
                    <span className="text-primary font-bold">{dailySales} transaksi</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    step="1"
                    value={dailySales}
                    onChange={(e) => setDailySales(Number(e.target.value))}
                    className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>1 transaksi</span>
                    <span>100 transaksi</span>
                  </div>
                </div>

                <div className="bg-black/40 rounded-xl p-4 border border-border/40">
                  <p className="text-[10px] text-muted-foreground mb-1">HARGA_MODAL (RESELLER)</p>
                  <p className="text-lg text-white font-bold">{formatPrice(basePrice)}</p>
                </div>
              </div>
            </div>

            <div className="bg-card/85 backdrop-blur-sm rounded-xl p-8 border border-border cyber-card relative overflow-hidden neon-green">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/40" />
              <h3 className="font-bold text-xl text-white uppercase mb-6 text-center tracking-wider">
                ESTIMASI_YIELD_KONTRAK
              </h3>
              
              <div className="space-y-4 font-mono text-xs">
                <div className="bg-black/30 rounded-xl p-4 flex justify-between items-center border border-border/20">
                  <span className="text-gray-300">Profit per Transaksi</span>
                  <span className="text-lg font-bold text-primary">
                    {formatPrice(profit.profitPerSale)}
                  </span>
                </div>
                <div className="bg-black/30 rounded-xl p-4 flex justify-between items-center border border-border/20">
                  <span className="text-gray-300">Profit per Hari</span>
                  <span className="text-lg font-bold text-secondary">
                    {formatPrice(profit.dailyProfit)}
                  </span>
                </div>
                <div className="bg-primary/5 rounded-xl p-6 border border-primary/30 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
                  <p className="text-center text-gray-300 mb-2 uppercase tracking-widest text-[10px]">ESTIMASI PROFIT BULANAN</p>
                  <p className="text-4xl font-bold text-center text-primary text-glow-green">
                    {formatPrice(profit.monthlyProfit)}
                  </p>
                </div>
              </div>

              <p className="text-[10px] text-muted-foreground text-center mt-4 font-mono uppercase">
                * Estimasi berdasarkan asumsi transmisi penjualan konsisten 30 hari
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-mono text-[10px] text-primary tracking-widest uppercase">TIERED_PARTNERSHIPS</span>
            </div>
            <h2 className="font-bold text-3xl md:text-4xl text-white uppercase mb-4 tracking-wider">
              Pilih Paket <span className="text-primary">Reseller</span>
            </h2>
            <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto mb-8">
              Pilih paket aliansi yang sesuai dengan kebutuhan bisnis top-up Anda
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 bg-card/80 rounded-full p-1.5 border border-border/80 font-mono text-xs">
              <button
                className={`px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-300 ${
                  billingPeriod === 'monthly' ? 'bg-primary text-black neon-green' : 'text-muted-foreground hover:text-white'
                }`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Bulanan
              </button>
              <button
                className={`px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${
                  billingPeriod === 'yearly' ? 'bg-primary text-black neon-green' : 'text-muted-foreground hover:text-white'
                }`}
                onClick={() => setBillingPeriod('yearly')}
              >
                Tahunan
                <span className="text-[9px] bg-primary/20 text-primary px-2 py-0.5 rounded-full border border-primary/20 animate-pulse">
                  -33%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PACKAGES.map((pkg) => {
              const Icon = pkg.icon;
              const price = billingPeriod === 'yearly' ? pkg.yearlyPrice : pkg.monthlyPrice;
              const isPopular = pkg.popular;
              
              return (
                <div 
                  key={pkg.id}
                  className={`relative bg-card/85 backdrop-blur-sm rounded-xl p-6 border transition-all duration-300 cyber-card overflow-hidden ${
                    isPopular 
                      ? 'border-primary shadow-[0_0_30px_rgba(var(--primary-rgb),0.15)] scale-105' 
                      : 'border-border/60 hover:border-primary/50'
                  }`}
                >
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/40" />

                  {isPopular && (
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-black text-[9px] font-mono font-bold px-4 py-1 rounded-b border-x border-b border-primary/30 uppercase tracking-widest">
                        POPULAR_TIER
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6 mt-2">
                    <div className={`w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4 neon-green`}>
                      <Icon className={`w-8 h-8 text-primary`} />
                    </div>
                    <h3 className="font-bold text-xl text-white uppercase tracking-wider">{pkg.name}</h3>
                  </div>

                  <div className="text-center mb-6 font-mono">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-2xl font-bold text-white">
                        {formatPrice(price)}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">
                      {billingPeriod === 'yearly' ? 'per tahun' : 'per bulan'}
                    </p>
                    {billingPeriod === 'yearly' && (
                      <p className="text-[10px] text-primary mt-1 font-bold">
                        Hemat {formatPrice(pkg.monthlyPrice * 12 - pkg.yearlyPrice)}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6 font-mono text-xs">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-white' : 'text-muted-foreground/50'}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full font-mono text-xs uppercase tracking-widest py-5 ${
                      isPopular 
                        ? 'bg-primary hover:bg-primary/80 text-black font-bold neon-green' 
                        : 'bg-white/5 hover:bg-white/10 text-white border border-border/80'
                    }`}
                    onClick={() => {
                      setSelectedPackage(pkg.id);
                      document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    [ Pilih {pkg.name} ]
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
              <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">TRANSMISSION_TESTIMONIALS</span>
            </div>
            <h2 className="font-bold text-3xl md:text-4xl text-white uppercase mb-4 tracking-wider">
              Wall of Fame: <span className="text-accent">Reseller Sukses</span>
            </h2>
            <p className="text-muted-foreground font-mono text-sm">
              Sinyal transmisi sukses dari komandan aliansi {theme.brand.name}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <div key={index} className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center neon-green">
                    <span className="font-bold text-primary font-mono">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <p className="font-bold text-white text-xs uppercase font-mono">{testimonial.name}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">{testimonial.store}</p>
                  </div>
                </div>
                
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                  ))}
                </div>

                <p className="text-gray-300 text-xs font-mono mb-4">"{testimonial.comment}"</p>
                
                <div className="bg-primary/5 rounded-lg p-3 border border-primary/20 relative overflow-hidden">
                  <p className="text-[9px] text-muted-foreground font-mono uppercase">ESTIMASI_YIELD</p>
                  <p className="font-mono text-base font-bold text-primary">{testimonial.profit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register-form" className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-card/80 backdrop-blur-sm rounded-xl p-8 border border-border cyber-card relative overflow-hidden">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/40" />

            {paymentStep === 'paying' && paymentData ? (
              /* Payment QRIS Display */
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4 neon-magenta">
                  <CreditCard className="w-6 h-6 text-accent" />
                </div>
                <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                  <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">PARTNER_DEPOSIT_WAIT</span>
                </div>
                <h2 className="font-bold text-xl text-white uppercase mb-1 tracking-wider">
                  Pembayaran Reseller
                </h2>
                <p className="text-muted-foreground text-xs font-mono mb-4">
                  Paket <span className="text-primary capitalize">{selectedPackage}</span> — {formatPrice(paymentData.amount)}
                </p>

                <div className="bg-white rounded-xl p-6 mb-4 flex justify-center border border-border/50">
                  {paymentData.qr_content ? (
                    <QRCodeCanvas value={paymentData.qr_content} size={200} level="M" marginSize={2} />
                  ) : (
                    <div className="w-[200px] h-[200px] flex items-center justify-center">
                      <CreditCard className="w-20 h-20 text-gray-300" />
                    </div>
                  )}
                </div>

                <p className="text-xs text-muted-foreground font-mono mb-4">
                  Scan QR code dengan e-wallet atau mobile banking terminal Anda
                </p>

                <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-lg p-3 mb-4">
                  <p className="text-[10px] text-yellow-400 font-mono flex items-center justify-center gap-2 uppercase tracking-wide">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Menunggu Konfirmasi... JANGAN TUTUP TRANSMISI
                  </p>
                </div>

                <Button variant="outline" className="w-full border-border text-muted-foreground hover:text-white font-mono text-xs uppercase"
                  onClick={() => { setPaymentStep('form'); setPaymentData(null); }}>
                  Ubah Saluran Energi
                </Button>
              </div>
            ) : (
              /* Registration Form */
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 neon-green">
                    <Gift className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-bold text-2xl text-white uppercase tracking-wider">
                    Daftar Sekarang
                  </h2>
                  <p className="text-muted-foreground text-xs font-mono mt-2">
                    Paket: <span className="text-primary capitalize font-bold">{selectedPackage}</span>
                    {' — '}
                    <span className="text-white">
                      {formatPrice(billingPeriod === 'yearly' 
                        ? PACKAGES.find(p => p.id === selectedPackage)?.yearlyPrice 
                        : PACKAGES.find(p => p.id === selectedPackage)?.monthlyPrice)}
                      /{billingPeriod === 'yearly' ? 'tahun' : 'bulan'}
                    </span>
                  </p>
                </div>

                {!user ? (
                  <div className="text-center">
                    <p className="text-muted-foreground text-xs font-mono mb-4">
                      Silakan login atau daftar terlebih dahulu untuk otorisasi terminal.
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button variant="outline" className="border-border text-white hover:bg-white/5 font-mono text-xs"
                        onClick={() => navigate('/login?redirect=/reseller')}>
                        Masuk
                      </Button>
                      <Button className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green text-xs"
                        onClick={() => navigate('/register')}>
                        Daftar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">▸ Commander Name</Label>
                      <Input type="text" value={user.name} disabled className="bg-black/30 border-border text-gray-400 font-mono text-xs" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">▸ Email</Label>
                      <Input type="email" value={user.email} disabled className="bg-black/30 border-border text-gray-400 font-mono text-xs" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs font-mono text-muted-foreground uppercase">▸ Nomor WhatsApp *</Label>
                      <Input id="phone" type="tel" placeholder="08xxxxxxxxxx" className="bg-black/50 border-border text-white font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary/30"
                        value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="reseller-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessName" className="text-xs font-mono text-muted-foreground uppercase">▸ Nama Toko/Usaha (Opsional)</Label>
                      <Input id="businessName" type="text" placeholder="Nama toko Anda" className="bg-black/50 border-border text-white font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary/30"
                        value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="reseller-business" />
                    </div>
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green text-xs"
                      disabled={loading} data-testid="reseller-submit">
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                          Memproses Transmisi...
                        </span>
                      ) : (
                        <>
                          <Rocket className="w-4 h-4 mr-2" />
                          Bayar & Daftar Reseller
                        </>
                      )}
                    </Button>
                    <div className="flex items-center gap-2 justify-center text-[10px] text-muted-foreground font-mono uppercase">
                      <Shield className="w-4 h-4" />
                      <span>Garansi uang kembali 7 hari</span>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 border-t border-border/40 cyber-grid">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-mono text-[10px] text-primary tracking-widest uppercase">FAQ_DIAGNOSTICS</span>
            </div>
            <h2 className="font-bold text-3xl text-white uppercase mb-4 tracking-wider">
              Pertanyaan Umum
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Apakah perlu deposit untuk menjadi reseller?',
                a: 'Tidak perlu deposit! Anda hanya perlu membayar biaya paket reseller sesuai pilihan Anda.'
              },
              {
                q: 'Berapa lama proses aktivasi akun reseller?',
                a: 'Proses aktivasi biasanya dilakukan dalam 1x24 jam setelah pembayaran dikonfirmasi.'
              },
              {
                q: 'Bagaimana cara menarik keuntungan?',
                a: 'Keuntungan dapat ditarik kapan saja ke rekening bank Anda. Proses pencairan maksimal 1x24 jam kerja.'
              },
              {
                q: 'Apakah ada garansi uang kembali?',
                a: 'Ya! Kami memberikan garansi uang kembali 100% dalam 7 hari pertama jika Anda tidak puas.'
              },
            ].map((faq, index) => (
              <div key={index} className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
                <h3 className="font-bold text-sm text-white mb-2 uppercase tracking-wide font-mono">▸ {faq.q}</h3>
                <p className="text-muted-foreground text-xs font-mono">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
